import { AxiosResponse } from 'axios';
import { FieldState, FormState } from 'formstate';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';

import { FormValidators, InputFieldState } from '@servicetitan/form';
import { NotificationsService, Status } from '@servicetitan/notifications';
import { inject, injectable } from '@servicetitan/react-ioc';

import { FeaturesStore } from '@servicetitan/application-data/features';
import { ExecutionResult, PhonesPro } from '@servicetitan/application-data/telecom';
import { PhoneNumbersStore } from './phone-numbers.store';
import { PlayGreetingStore } from './play-greeting.store';

@injectable()
export class DefaultNumbersStore {
    @observable savedCallContinuityNumber?: string;
    @observable savedDefaultSmsNumber?: string;
    @observable savedDefaultMmsNumber?: string;
    @observable isCallContinuityNumberFieldDisabled = true;
    @observable isDefaultSmsNumberFieldDisabled = true;
    @observable isDefaultMmsNumberFieldDisabled = true;
    @observable isActionButtonsDisabled = false;

    formState = new FormState({
        callContinuityNumber: new InputFieldState<string>('')
            .validators((value: string) => {
                if (this.featureStore.features.EnableServiceTitanPhonesUser) {
                    return FormValidators.required(value);
                }
            })
            .disableAutoValidation(),
        defaultSmsNumber: new InputFieldState<string>(''),
        defaultMmsNumber: new InputFieldState<string>(''),
    });

    constructor(
        @inject(PhonesPro) private phonesProApi: PhonesPro,
        @inject(PhoneNumbersStore) private phoneNumbersStore: PhoneNumbersStore,
        @inject(PlayGreetingStore) private playGreetingStore: PlayGreetingStore,
        @inject(FeaturesStore) private featureStore: FeaturesStore,
        @inject(NotificationsService) private notifications: NotificationsService
    ) {
        makeObservable(this);
    }

    @action loadData = async () => {
        const response = await this.phonesProApi.getPhoneSettings();
        if (!response || !response.data) {
            return;
        }

        runInAction(() => {
            const data = response!.data!;
            this.savedCallContinuityNumber = data.defaultForwardingNumber || '';
            this.savedDefaultSmsNumber = data.defaultSmsNumber || '';
            this.savedDefaultMmsNumber = data.defaultMmsNumber || '';
            this.playGreetingStore.init(data.playGreetingModel!);
            this.resetDefaultNumbers();
        });
    };

    @computed get defaultNumbersFormHasChanges() {
        return (
            this.formState.$.callContinuityNumber.dirty ||
            this.formState.$.defaultSmsNumber.dirty ||
            this.formState.$.defaultMmsNumber.dirty ||
            this.playGreetingStore.formState.$.playGreeting.dirty
        );
    }

    @action enableCallContinuityField = () => (this.isCallContinuityNumberFieldDisabled = false);
    @action disableCallContinuityField = () => (this.isCallContinuityNumberFieldDisabled = true);
    @action enableDefaultSmsNumberField = () => (this.isDefaultSmsNumberFieldDisabled = false);
    @action disableDefaultSmsNumberField = () => (this.isDefaultSmsNumberFieldDisabled = true);
    @action enableDefaultMmsNumberField = () => (this.isDefaultMmsNumberFieldDisabled = false);
    @action disableDefaultMmsNumberField = () => (this.isDefaultMmsNumberFieldDisabled = true);

    @action resetDefaultNumbers = () => {
        this.formState.$.callContinuityNumber.reset(this.savedCallContinuityNumber);
        this.formState.$.defaultSmsNumber.reset(this.savedDefaultSmsNumber);
        this.formState.$.defaultMmsNumber.reset(this.savedDefaultMmsNumber);
        this.playGreetingStore.reset();
    };

    @action handleFieldChange = <T extends AxiosResponse<ExecutionResult | null>>(
        field: FieldState<any>,
        response: T,
        action: (result: any) => void
    ) => {
        if (response?.data) {
            if (response.data.success) {
                action(response.data.result);
            } else {
                field.setError(response.data.message || '');
            }
        }
    };

    @action saveDefaultNumbers = async () => {
        try {
            this.isActionButtonsDisabled = true;

            if ((await this.formState.validate()).hasError) {
                return;
            }

            const form = this.formState.$;

            if (form.callContinuityNumber.dirty) {
                this.handleFieldChange(
                    form.callContinuityNumber,
                    await this.phonesProApi.saveCallContinuityNumber(
                        form.callContinuityNumber.value
                    ),
                    result => {
                        this.savedCallContinuityNumber = result || '';
                        this.phoneNumbersStore.loadPhoneNumbers();
                    }
                );
            }

            if (form.defaultSmsNumber.dirty) {
                this.handleFieldChange(
                    form.defaultSmsNumber,
                    await this.phonesProApi.editDefaultSmsNumber(form.defaultSmsNumber.value),
                    () => {
                        this.savedDefaultSmsNumber = form.defaultSmsNumber.value;
                    }
                );
            }

            if (form.defaultMmsNumber.dirty) {
                this.handleFieldChange(
                    form.defaultMmsNumber,
                    await this.phonesProApi.editDefaultMmsNumber(form.defaultMmsNumber.value),
                    () => {
                        this.savedDefaultMmsNumber = form.defaultMmsNumber.value;
                    }
                );
            }

            await this.playGreetingStore.save();

            if (!this.formState.hasFieldError) {
                this.resetDefaultNumbers();
            }
        } catch {
            this.notifications.show({
                status: Status.Error,
                title: 'Phone Settings',
                message: 'An error occurred, please try again.',
            });
        } finally {
            runInAction(() => (this.isActionButtonsDisabled = false));
        }
    };
}
